import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Avatar,
  IconButton,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  Chip
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { apiService } from '../../utils/api';
import { normalizeImageUrl } from '../Post';

/**
 * ReviewReplySection - クチコミの返信表示・投稿セクション
 */
const ReviewReplySection = ({
  reviewId,
  currentUserId,
  isOwner,
  isTaggedCorp,
  onRefresh
}) => {
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [replyText, setReplyText] = useState('');
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedReplyId, setSelectedReplyId] = useState(null);

  useEffect(() => {
    fetchReplies();
  }, [reviewId]);

  const fetchReplies = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/api/reviews/${reviewId}/replies`);
      const data = response?.data || response || {};
      setReplies(data.replies || []);
    } catch (err) {
      setError(err.response?.data?.error || '返信の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReply = async () => {
    if (replyText.length < 2) {
      setError('返信は2文字以上で入力してください');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      await apiService.post('/api/review-replies', {
        review_id: reviewId,
        text: replyText.trim()
      });
      setReplyText('');
      setShowReplyForm(false);
      await fetchReplies();
      if (onRefresh) onRefresh();
    } catch (err) {
      setError(err.response?.data?.error || '返信の投稿に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReply = async (replyId) => {
    if (window.confirm('この返信を削除しますか？')) {
      try {
        await apiService.delete(`/api/review-replies/${replyId}`);
        setAnchorEl(null);
        await fetchReplies();
        if (onRefresh) onRefresh();
      } catch (err) {
        setError(err.response?.data?.error || '返信の削除に失敗しました');
      }
    }
  };

  const handleMenuOpen = (e, replyId) => {
    setAnchorEl(e.currentTarget);
    setSelectedReplyId(replyId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReplyId(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ja-JP', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const canReply = isOwner || isTaggedCorp;

  return (
    <Box sx={{ p: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* 返信一覧 */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : replies.length > 0 ? (
        <Box sx={{ mb: 2 }}>
          {replies.map((reply) => (
            <Box
              key={reply.id}
              sx={{
                display: 'flex',
                gap: 1,
                mb: 1.5,
                pl: 2,
                borderLeft: '2px solid',
                borderColor: 'divider'
              }}
            >
              <Avatar
                src={normalizeImageUrl(reply.replier_avatar)}
                alt={reply.replier_name}
                sx={{ width: 32, height: 32, flexShrink: 0 }}
              />
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {reply.replier_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {formatDate(reply.created_at)}
                    </Typography>
                  </Box>
                  {reply.is_deletable && (
                    <>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, reply.id)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        open={selectedReplyId === reply.id && !!anchorEl}
                        onClose={handleMenuClose}
                      >
                        <MenuItem
                          onClick={() => {
                            handleDeleteReply(reply.id);
                            handleMenuClose();
                          }}
                          sx={{ color: 'error.main' }}
                        >
                          削除
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                </Box>
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                  {reply.text}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      ) : null}

      {/* 返信フォーム */}
      {canReply && (
        <Box>
          {!showReplyForm ? (
            <Button
              size="small"
              variant="text"
              startIcon={<MoreVertIcon sx={{ transform: 'scaleX(-1)' }} />}
              onClick={() => setShowReplyForm(true)}
            >
              返信を書く
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="返信を入力..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                disabled={submitting}
                size="small"
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleSubmitReply}
                disabled={submitting || replyText.length < 2}
              >
                {submitting ? '投稿中...' : '送信'}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setShowReplyForm(false);
                  setReplyText('');
                  setError('');
                }}
                disabled={submitting}
              >
                キャンセル
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ReviewReplySection;
